import { hotSellGoods, resetHotSellLimit, allActivePromotions } from '@/api/basic/common'
import { retailConfigDetail } from '@/api/setting'
import { SettingStorage } from '@/utils/storage'
import { unionBy, groupBy, intersectionBy, differenceBy } from 'lodash-es'
import { projectName } from '@/config'
import Vue from 'vue'

const state = () => ({
  product: null, // 扫码商品数据
  singleProductPromo: null, // 单品促销数据
  wholeOrderPromo: null, // 整单促销数据
  discountAndPointsExclusionSetting: null, // 不打折不积分商品设置数据
})

const mutations = {
  /**
   * 创建数据仓库
   * @param {*} state
   * @param {*} storeName
   */
  createDataStore(state, storeName) {
    if (!state[storeName]) {
      state[storeName] = LocalForage.createInstance({ name: projectName, storeName })
    }
  },
}

const actions = {
  /**
   * 清除缓存，重置初始化条件
   */
  async cleanAndReset() {
    LocalForage.clear()
    await resetHotSellLimit()
  },

  /**
   * 初始化热销商品到本地数据库
   */
  async initHotSellToDB() {
    try {
      // 获取设置中的是否缓存的开关
      let setting = SettingStorage.get()

      if (!setting) {
        const { data: settingConfig } = await retailConfigDetail()
        setting = settingConfig
        SettingStorage.set(settingConfig)
      }

      if (setting?.ifCacheTopSku) {
        const { data } = await hotSellGoods()
        if (data) {
          await Promise.all(Object.entries(data).map(([key, value]) => LocalForage.setItem(key, value)))
        }
      }
    } catch (error) {
      console.error('初始化商品本地存储失败：', error)
    }
  },

  /**
   * 增量更新热销商品 - 当价格、库存等信息变动时
   */
  async incrementUpdateHotSell() {
    try {
      const { data } = await hotSellGoods()

      if (data && Object.keys(data).length) {
        for (let i = 0; i < Object.keys(data).length; i++) {
          const key = Object.keys(data)[i]

          // 已经存在的 key，更新 value
          const skus = await LocalForage.getItem(key)
          if (skus) {
            const updated = unionBy(data[key], skus, 'skuId')
            await LocalForage.setItem(key, updated)
          } else {
            await LocalForage.setItem(key, data[key])
          }
        }
      }
    } catch (error) {
      console.error('增量更新商品本地存储失败：', error)
    }
  },

  /**
   * 从接口获取所有当前登录门店且活动时间范围包含当前时间的促销数据
   * payload 若未传入，则需要调用接口
   */
  async initPromoToDB({ commit, state, rootGetters }, payload = null) {
    try {
      console.time('促销数据初始化')

      commit('createDataStore', 'wholeOrderPromo')
      commit('createDataStore', 'singleProductPromo')
      commit('createDataStore', 'discountAndPointsExclusionSetting')

      let promos = payload
      if (!promos) {
        const { data } = await allActivePromotions({ shopCode: rootGetters['user/shopCode'] })
        promos = data
      }

      // 不打折、不积分商品
      if (promos?.discountInfos?.length) {
        // 根据 skuCode + smallCategoryCode 为 key进行分组(skuCode 可能为 * 号)
        const objBySmallCategory = groupBy(promos.discountInfos, (item) => `${item.skuCode}${item.smallCategoryCode}`)

        // 获取本地数据库中已存在的所有key
        const existingKeys = await state.discountAndPointsExclusionSetting.keys()

        // 找出需要删除的key(在本地存在但新数据中不存在的key)
        const keysToRemove = existingKeys?.filter((key) => !objBySmallCategory[key]) ?? []

        // 删除过期数据
        if (keysToRemove?.length) {
          await Promise.all(keysToRemove.map((key) => state.discountAndPointsExclusionSetting.removeItem(key)))
        }

        // 更新/新增数据
        await Promise.all(
          Object.entries(objBySmallCategory).map(([key, value]) =>
            state.discountAndPointsExclusionSetting.setItem(key, value)
          )
        )
      } else {
        await state.discountAndPointsExclusionSetting.clear()
      }

      // 单品促销数据
      if (promos?.singleInfos?.length) {
        // 根据 skuCode 为 key，将数据进行分组。
        const promoObjectBySkuCode = groupBy(promos.singleInfos, 'skuCode')

        const existingKeys = await state.singleProductPromo.keys()

        const keysToRemove = existingKeys?.filter((key) => !promoObjectBySkuCode[key]) ?? []

        // 移除过期数据
        if (keysToRemove?.length) {
          await Promise.all(keysToRemove.map((key) => state.singleProductPromo.removeItem(key)))
        }

        // 更新/新增数据
        await Promise.all(
          Object.entries(promoObjectBySkuCode).map(([key, value]) => state.singleProductPromo.setItem(key, value))
        )
      } else {
        await state.singleProductPromo.clear()
      }

      // 整单促销数据
      if (promos?.billHitInfos?.length) {
        // 根据 promotionId 为 key 进行分组
        const promoObjectByPromotionId = groupBy(promos.billHitInfos, 'promotionId')

        const existingKeys = await state.wholeOrderPromo.keys()
        
        const keysToRemove = existingKeys?.filter(key => !promoObjectByPromotionId[key]) ?? []

        // 移除过期数据
        if (keysToRemove?.length) {
          await Promise.all(keysToRemove.map(key => state.wholeOrderPromo.removeItem(key)))
        }

        // 按照接口顺序更新数据
        for (let i = 0; i < promos.billHitInfos.length; i++) {
          const item = promos.billHitInfos[i]
          await state.wholeOrderPromo.setItem(item.promotionId, item)
        }
      } else {
        await state.wholeOrderPromo.clear()
      }

      console.timeEnd('促销数据初始化')
    } catch (error) {
      console.error('初始化促销本地存储失败：', error)
    }
  },

  /**
   * 单品促销命中判定
   */
  async singleProductPromoHit({ state }, data) {
    console.time('单品促销判定耗时')

    // 用于缓存已查询的促销数据
    const promoCache = new Map()
    const settingCache = new Map()

    // 构造判定返回数据
    let responseData = {
      isHitted: false,
      singles: data.items.map(({ skuCode, skuBarcode, packageMode, packageBarcode }) => ({
        skuCode,
        skuBarcode,
        packageMode,
        packageBarcode,
        promotionType: null,
        promotionCode: null,
        promotionName: null,
        promotionPrice: null,
        discountRatio: null,
        isHitted: false,
        fixedPrice: null,
        singleLimit: null,
        ifNoDiscount: null,
        ifNoRewardPoints: null,
        giftPlanNo: null,
        isNoDiscountV2: false,
        isNoReturnV2: false,
        isNoRewardV2: false,
      })),
    }

    // 0-6
    const currentWeek = Vue.prototype.dayjs().day()
    // 1-31
    const currentDate = Vue.prototype.dayjs().date()
    const cycleType = {
      DAY: () => true,
      WEEK: (setting) => setting.includes(currentWeek === 0 ? 7 : currentWeek),
      MONTH: (setting) => setting.includes(currentDate),
    }

    try {
      for (let i = 0; i < data.items.length; i++) {
        const sku = data.items[i]
        const single = responseData.singles[i]

        // 从缓存获取促销数据,没有则查询并缓存
        let promotions = promoCache.get(sku.skuCode)
        if (!promotions) {
          promotions = await state.singleProductPromo?.getItem(sku.skuCode)
          promoCache.set(sku.skuCode, promotions)
        }

        // 从缓存获取商品设置,没有则查询并缓存
        const settingKey = `${sku.skuCode}${sku.categoryCode}`
        let productSetting = settingCache.get(settingKey)
        if (!productSetting) {
          productSetting = await state.discountAndPointsExclusionSetting?.getItem(settingKey)
          if (!productSetting) {
            productSetting = await state.discountAndPointsExclusionSetting?.getItem(`*${sku.categoryCode}`)
          }
          settingCache.set(settingKey, productSetting)
        }

        // 设置不打折不积分
        single.isNoDiscountV2 = productSetting ? productSetting.some((i) => i.disoucntType === 'NO_DISCOUNT') : false
        single.isNoRewardV2 = productSetting ? productSetting.some((i) => i.disoucntType === 'NO_REWARD') : false

        if (promotions) {
          // 促销判定
          const filtered = promotions.filter((promo) => {
            return (
              promo.participant.includes(data.participant) &&
              cycleType[promo.promotionCycleType](promo.promotionCycleSetting) &&
              sku.saleQty >= promo.singleLimit
            )
          })

          if (filtered.length) {
            const now = Date.now()
            const closestPromotion = filtered.reduce((closest, current) => {
              const currentDiff = Math.abs(current.activityBegin - now)
              const closestDiff = Math.abs(closest.activityBegin - now)
              return currentDiff < closestDiff ? current : closest
            })

            responseData.isHitted = true
            single.isHitted = true
            single.promotionCode = closestPromotion.promotionCode
            single.promotionName = closestPromotion.promotionName
            single.promotionType = closestPromotion.promotionType
            single.promotionPrice = closestPromotion.promotionPrice
            single.singleLimit = closestPromotion.singleLimit
            single.isNoDiscountV2 = null
            single.isNoReturnV2 = null
            single.isNoRewardV2 = null
            single.ifNoDiscount = closestPromotion.ifNoDiscount
            single.ifNoRewardPoints = closestPromotion.ifNoRewardPoints
            single.discountRatio = closestPromotion.discountRatio
            single.fixedPrice = closestPromotion.fixedPrice
          }
        }
      }
    } finally {
      cycleType.DAY = null
      cycleType.WEEK = null
      cycleType.MONTH = null
      promoCache.clear()
      settingCache.clear()
    }
    console.timeEnd('单品促销判定耗时')
    return responseData
  },

  /**
   * 整单促销命中判定
   */
  async wholeOrderPromoHit({ state }, data) {
    console.time('整单促销判定耗时')
    const allSkus = data.items
    // 构造判定返回数据
    let responseData = {
      isHitted: false,
      items: null,
    }

    // 并行获取所有促销数据
    let allPromos = []

    const keys = await state.wholeOrderPromo?.keys()

    if (!keys?.length) {
      console.timeEnd('整单促销判定耗时')
      return responseData
    }

    allPromos = await Promise.all(keys.map((key) => state.wholeOrderPromo?.getItem(key)))

    // 在商品范围内的商品
    const inProductScope = {
      ALL_PRODUCT: () => {
        // 全部商品
        return allSkus
      },

      INCLUDE_PRODUCT: (scopes) => {
        // 求交集
        return intersectionBy(allSkus, scopes, 'skuCode')
      },

      EXCLUDE_PRODUCT: (scopes) => {
        // 排除
        return differenceBy(allSkus, scopes, 'skuCode')
      },
    }

    // 创建计算函数，对在商品范围内的商品金额求和
    const sumCalculator = (items) => {
      return items.reduce((sum, curr) => Number(BN.plus(sum, curr.postBillZkAmt).toFixed(2)), 0)
    }

    const currentWeek = Vue.prototype.dayjs().day()
    const currentDate = Vue.prototype.dayjs().date()
    const cycleType = {
      DAY: () => true,
      WEEK: (setting) => setting.includes(currentWeek === 0 ? 7 : currentWeek),
      MONTH: (setting) => setting.includes(currentDate),
    }

    try {
      // 1. 判定促销对象是否满足条件
      // 2. 判定促销周期是否满足条件
      // 3. 根据商品范围判定是否满足金额门槛
      const filtered = allPromos.filter((promo) => {
        return (
          promo.participant.includes(data.participant) &&
          cycleType[promo.promotionCycleType](promo.promotionCycleSetting) &&
          sumCalculator(inProductScope[promo.productScope](promo.skuItems)) >= promo.billPromotionThresholdAmt
        )
      })
      if (filtered?.length) {
        responseData.isHitted = true
        responseData.items = filtered.map((item) => ({
          billInstantRebateAmt: item.billInstantRebateAmt,
          billPromotionThresholdAmt: item.billPromotionThresholdAmt,
          billZkRate: item.billZkRate,
          promotionCode: item.promotionCode,
          promotionName: item.promotionName,
          promotionType: item.promotionType,
          skuItems: inProductScope[item.productScope](item.skuItems).map(({ skuCode, skuId }) => ({ skuCode, skuId })),
        }))
      }
    } finally {
      inProductScope.ALL_PRODUCT = null
      inProductScope.EXCLUDE_PRODUCT = null
      inProductScope.INCLUDE_PRODUCT = null
      cycleType.DAY = null
      cycleType.MONTH = null
      cycleType.WEEK = null
    }

    console.timeEnd('整单促销判定耗时')
    return responseData
  },
}
export default { state, mutations, actions }
